import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getClientes } from "@/lib/api";
import { nombreVisible, nombreLegal, coincideBusqueda } from "@/lib/clienteNombre";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";

/** Paso 1 de «nuevo chat»: elegir una ficha de cliente sin chat o crear un cliente nuevo. */
export function NuevoChatDialog({ open, onOpenChange, onElegir, onCrearCliente }) {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setQ("");
    setLoading(true);
    getClientes()
      .then((d) => setClientes((d.clientes || []).filter((c) => !c.chat_activo)))
      .catch(() => toast.error("No se pudieron cargar los clientes"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtrados = useMemo(() => {
    const lista = clientes.filter((c) => coincideBusqueda(c, q));
    // Activos primero
    return lista.sort((a, b) => (a.estado === "inactivo") - (b.estado === "inactivo"));
  }, [clientes, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Nuevo chat de cliente</DialogTitle>
          <DialogDescription>Elige el cliente o créalo si aún no existe.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente" className="pl-9" />
        </div>
        <ul className="flex-1 overflow-y-auto -mx-2 min-h-[200px]">
          <li>
            <button type="button" onClick={onCrearCliente} className="w-full flex items-center gap-3 px-2 h-14 rounded-md text-left hover:bg-muted/60">
              <span className="h-10 w-10 rounded-xl border border-dashed flex items-center justify-center text-muted-foreground">
                <Plus className="w-4 h-4" />
              </span>
              <span className="text-sm font-medium">Crear cliente nuevo{q.trim() ? ` «${q.trim()}»` : ""}</span>
            </button>
          </li>
          {loading && (
            <li className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </li>
          )}
          {filtrados.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => onElegir(c)} className="w-full flex items-center gap-3 px-2 h-14 rounded-md text-left hover:bg-muted/60">
                <ClienteLogo cliente={c} size={40} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{nombreVisible(c)}</span>
                  <span className="block text-xs text-muted-foreground truncate">{nombreLegal(c) || c.ciudad}</span>
                </span>
                {c.estado === "inactivo" && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Inactivo</span>}
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
